import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure } from "../../../telemetry";
import type { CalEvent, WorkingHours } from "../types";
import { findSlots, finderRange, SEARCH_WORKDAYS, type Candidate } from "../domain/freeBusy";
import { dayKeyOf, dayLabel, dayName, hm, rangeLabel } from "../domain/time";
import { useAvailability, type LoadSchedules } from "../hooks/useAvailability";
import { fetchCalendarView } from "../services/calendarView";
import { CheckIcon, ClockIcon, WarnIcon } from "./CalendarIcons";
import styles from "./CalendarModule.module.css";

/** Times shown before "Show more", and the most shown after it (the mockup's 6 and 40). */
const SLOTS_SHOWN = 6;
const SLOTS_MAX = 40;

interface TimeFinderProps {
  /** The card above the times: what is being scheduled. */
  readonly summary: ReactNode;
  /** Colleagues whose free/busy counts (not you). */
  readonly people: readonly string[];
  /** Display names for `people`, for "Priya is busy". */
  readonly names: ReadonlyMap<string, string>;
  readonly durationMin: number;
  /** The meeting being moved: its current time isn't offered and it doesn't count as busy. Null for a new meeting. */
  readonly moving: CalEvent | null;
  readonly now: Date;
  readonly workingHours: WorkingHours;
  readonly token: string;
  readonly loadSchedules: LoadSchedules;
  /** Under a chosen time, e.g. "Selected — confirm below". */
  readonly selectedLine: string;
  /** The band that appears once a time is chosen: its title, the action, and anything said under it. */
  readonly pickedTitle: string;
  readonly pickedDetail?: string;
  readonly confirmLabel: string;
  readonly confirmDisabled?: boolean;
  readonly pickedFooter?: ReactNode;
  readonly onConfirm: (start: Date, end: Date) => void;
  /** The chosen time was cleared or changed (so a half-prepared action can be dropped). */
  readonly onPickChange?: () => void;
}

type OwnCalendar = { readonly events: readonly CalEvent[] } | "loading" | "error";

/**
 * The mockup's time finder: free times over the next few working days, with
 * "Everyone free" or "Include near-misses" (one person busy). Your calendar is
 * read for the whole search span, not just the week on screen; anyone whose
 * calendar couldn't be read is never counted as free.
 */
export function TimeFinder({
  summary,
  people,
  names,
  durationMin,
  moving,
  now,
  workingHours,
  token,
  loadSchedules,
  selectedLine,
  pickedTitle,
  pickedDetail,
  confirmLabel,
  confirmDisabled,
  pickedFooter,
  onConfirm,
  onPickChange,
}: Readonly<TimeFinderProps>) {
  const { refreshGraphToken } = useAuthState();
  const today = dayKeyOf(now);
  const range = useMemo(() => finderRange(today), [today]);
  const schedules = useAvailability(loadSchedules, people, range.start, range.end);
  const [own, setOwn] = useState<OwnCalendar>("loading");
  const [attempt, setAttempt] = useState(0);
  const [near, setNear] = useState(false);
  const [more, setMore] = useState(false);
  const [picked, setPicked] = useState<Candidate | null>(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    let cancelled = false;
    setOwn("loading");
    fetchCalendarView(tokenRef.current, () => refreshGraphToken(false), range.start, range.end)
      .then((result) => {
        if (!cancelled) setOwn({ events: result.events });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setOwn("error");
        logConnectorFailure("calendar", e, "load-finder");
      });
    return () => {
      cancelled = true;
    };
  }, [range, refreshGraphToken, attempt]);

  const found = useMemo(
    () =>
      typeof own === "object" && schedules
        ? findSlots({ events: own.events, moving, people, schedules, durationMin, hours: workingHours, now })
        : null,
    [own, schedules, moving, people, durationMin, workingHours, now],
  );

  const firstName = (email: string) => (names.get(email) ?? email).trim().split(/\s+/)[0] ?? email;
  const pick = (c: Candidate | null) => {
    setPicked(c);
    onPickChange?.();
  };

  if (own === "error") {
    return (
      <>
        {summary}
        <div className={`${styles.band} ${styles.bandErr}`} role="alert">
          <div className={styles.bandTitle}>
            <WarnIcon />
            Couldn't Load Your Calendar
          </div>
          <div className={styles.bandText}>Free times can't be worked out without it.</div>
          <div className={styles.bandActions}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setAttempt((n) => n + 1)}>
              Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!found) {
    return (
      <>
        {summary}
        <div className={styles.skelStack}>
          <div className={styles.skel} />
          <div className={styles.skel} />
          <div className={styles.skel} />
        </div>
      </>
    );
  }

  const { unknown } = found;
  const best = found.slots.filter((c) => c.busy.length === 0);
  const nearMisses = found.slots.filter((c) => c.busy.length === 1);
  const list = near ? [...best, ...nearMisses].sort((a, b) => a.start.getTime() - b.start.getTime()) : best;
  const shown = list.slice(0, more ? SLOTS_MAX : SLOTS_SHOWN);

  const others = people.length;
  const hint = searchHint(others, unknown.length, workingHours);

  let freeLine = `Everyone free · ${others + 1} people`;
  if (others === 0) freeLine = "You're free";
  else if (unknown.length) freeLine = `You're free · availability unknown for ${unknown.length} of ${others}`;

  const rows: ReactNode[] = [];
  let lastDay = "";
  for (const c of shown) {
    const day = dayKeyOf(c.start);
    if (day !== lastDay) {
      rows.push(
        <div key={`h-${day}`} className={styles.dayH}>
          {dayName(day, today)}
        </div>,
      );
      lastDay = day;
    }
    rows.push(
      <SlotButton
        key={c.start.getTime()}
        slot={c}
        on={picked?.start.getTime() === c.start.getTime()}
        freeLine={freeLine}
        selectedLine={selectedLine}
        firstName={firstName}
        onPick={pick}
      />,
    );
  }

  const choose = (value: boolean) => {
    setNear(value);
    setMore(false);
    pick(null);
  };

  return (
    <>
      {summary}
      <div className={styles.sec}>
        <div className={styles.secH}>
          <span className={styles.secBar} />
          <span className={styles.secTitle}>{near ? "Suggested Times" : "Everyone Free"}</span>
          <span className={`${styles.secRight} ${styles.secMeta}`}>{list.length}</span>
        </div>
        <div className={styles.chips}>
          <button type="button" className={`${styles.chip} ${!near ? styles.chipOn : ""}`} aria-pressed={!near} onClick={() => choose(false)}>
            Everyone free · {best.length}
          </button>
          <button type="button" className={`${styles.chip} ${near ? styles.chipOn : ""}`} aria-pressed={near} onClick={() => choose(true)}>
            Include near-misses · {nearMisses.length}
          </button>
        </div>
        <span className={styles.hint}>{hint}</span>
        {shown.length === 0 && (
          <div className={styles.empty}>
            <b>No shared free time in the next {SEARCH_WORKDAYS} working days</b>
            {near ? "Look further ahead in Outlook" : "Include near-misses to see times when one person is busy"}
          </div>
        )}
        {rows}
        {list.length > SLOTS_SHOWN && !more && (
          <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnDashed}`} onClick={() => setMore(true)}>
            Show {Math.min(SLOTS_MAX, list.length) - SLOTS_SHOWN} More
          </button>
        )}
      </div>
      {picked && (
        <div className={`${styles.band} ${styles.bandAi} ${styles.bandSticky}`}>
          <div className={styles.bandTitle}>
            <CheckIcon />
            {pickedTitle}
          </div>
          <div className={styles.bandText}>
            <b>
              {dayLabel(dayKeyOf(picked.start))} · {rangeLabel(picked.start, picked.end)}
            </b>
            {pickedDetail ? ` · ${pickedDetail}` : ""}
          </div>
          <div className={styles.bandActions}>
            <button type="button" className={styles.btn} disabled={confirmDisabled} onClick={() => onConfirm(picked.start, picked.end)}>
              {confirmLabel}
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => pick(null)}>
              Clear
            </button>
          </div>
          {pickedFooter}
        </div>
      )}
    </>
  );
}

/** The line under the chips: whose free/busy was searched, in which hours, and how many couldn't be read. */
function searchHint(others: number, unknownCount: number, workingHours: WorkingHours): string {
  const hours = `${hm(workingHours.startMin)}–${hm(workingHours.endMin)}`;
  const otherWord = others === 1 ? "other" : "others";
  let hint =
    others === 0 ? `Your free time, ${hours}, ranked by time` : `Free/busy for you and ${others} ${otherWord}, ${hours}, ranked by time`;
  if (unknownCount > 0) hint += ` · ${unknownCount} couldn't be read, so aren't counted as free`;
  return hint;
}

interface SlotButtonProps {
  readonly slot: Candidate;
  readonly on: boolean;
  /** Under a time when everyone is free. */
  readonly freeLine: string;
  readonly selectedLine: string;
  readonly firstName: (email: string) => string;
  readonly onPick: (slot: Candidate) => void;
}

/** One offered time: free, a near-miss (one person busy), or the one chosen. */
function SlotButton({ slot, on, freeLine, selectedLine, firstName, onPick }: Readonly<SlotButtonProps>) {
  const isNear = slot.busy.length > 0;
  let icon = isNear ? <WarnIcon /> : <ClockIcon />;
  let sub = isNear ? `${firstName(slot.busy[0] ?? "")} is busy` : freeLine;
  if (on) {
    icon = <CheckIcon />;
    sub = selectedLine;
  }
  return (
    <button
      type="button"
      className={`${styles.slot} ${on ? styles.slotOn : ""} ${isNear ? styles.slotNear : ""}`}
      aria-pressed={on}
      onClick={() => onPick(slot)}
    >
      <span className={styles.slotIcon}>{icon}</span>
      <span className={styles.optBody}>
        <span className={styles.slotTime}>{rangeLabel(slot.start, slot.end)}</span>
        <span className={styles.slotSub}>{sub}</span>
      </span>
    </button>
  );
}
